import type { Job } from './graphql';

export const activeJobStatuses = new Set(['pending', 'running', 'awaiting_qc', 'awaiting_upstream', 'awaiting_cost_approval']);

export interface PresignedUpload {
  uploadUrl: string;
  uploadFields?: string;
  uploadMethod?: string;
}

export function hasActiveJobs(jobRows: Job[]): boolean {
  return jobRows.some((job) => activeJobStatuses.has(job.status));
}

export function shouldPollPipeline(jobRows: Job[], watchUntil: number): boolean {
  return hasActiveJobs(jobRows) || Date.now() < watchUntil;
}

export function parseUploadFields(uploadFields?: string): Record<string, string> {
  if (!uploadFields) return {};
  const parsed = JSON.parse(uploadFields) as Record<string, unknown>;
  return Object.fromEntries(Object.entries(parsed).map(([key, value]) => [key, String(value)]));
}

export function buildPresignedPostFormData(uploadFields: Record<string, string>, file: File): FormData {
  const formData = new FormData();
  for (const [key, value] of Object.entries(uploadFields)) {
    formData.append(key, value);
  }
  const signedContentType = uploadFields['Content-Type'];
  const uploadFile = signedContentType && file.type !== signedContentType
    ? new Blob([file], { type: signedContentType })
    : file;
  formData.append('file', uploadFile, file.name);
  return formData;
}

export async function uploadPresignedPost(
  upload: PresignedUpload,
  file: File,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const uploadFields = parseUploadFields(upload.uploadFields);
  const method = upload.uploadMethod || 'POST';
  const signedContentType = uploadFields['Content-Type'];
  const uploadFile = signedContentType && file.type !== signedContentType
    ? new Blob([file], { type: signedContentType })
    : file;
  const body = method === 'PUT' ? uploadFile : buildPresignedPostFormData(uploadFields, file);
  const uploadRes = await fetchImpl(upload.uploadUrl, {
    method,
    ...(method === 'PUT' ? { headers: uploadFields } : {}),
    body,
  });
  if (!uploadRes.ok) {
    const detail = await uploadRes.text().catch(() => '');
    throw new Error(`S3 upload failed: ${uploadRes.status}${detail ? ` ${detail}` : ''}`);
  }
}
